import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { AiFillEye, AiOutlineCopy } from "react-icons/ai";
import { FaCode } from "react-icons/fa";

import { AppWrap, MotionWrap } from "../../wrapper";
import { urlFor, client } from "../../client";
import "./Project.scss";
import ProjectPreview from "./ProjectPreview";
import ProjectModal from "./ProjectModal";
import QuickLinks from "../Footer/QuickLinks";

// Lazy Image Component
const LazyImage = ({ src, alt, className, onLoad }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const imgRef = useRef();

  useEffect(() => {
    const imageObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !imageSrc) {
            setImageSrc(src);
          }
        });
      },
      {
        rootMargin: "50px", // Start loading 50px before the image is visible
        threshold: 0.1,
      }
    );

    if (imgRef.current) {
      imageObserver.observe(imgRef.current);
    }

    return () => {
      if (imgRef.current) {
        imageObserver.unobserve(imgRef.current);
      }
    };
  }, [src, imageSrc]);

  const handleImageLoad = () => {
    setImageLoaded(true);
    if (onLoad) onLoad();
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(true);
  };

  return (
    <div ref={imgRef} className={`lazy-image-container ${className || ""}`}>
      {!imageLoaded && !imageError && (
        <div className="image-placeholder">
          <div className="loading-spinner"></div>
        </div>
      )}
      {imageSrc && (
        <img
          src={imageSrc}
          alt={alt}
          onLoad={handleImageLoad}
          onError={handleImageError}
          loading="lazy"
          style={{
            opacity: imageLoaded ? 1 : 0,
            transition: "opacity 0.3s ease",
          }}
        />
      )}
      {imageError && (
        <div className="image-error">
          <span>Failed to load image</span>
        </div>
      )}
    </div>
  );
};

const Project = () => {
  const [projects, setProjects] = useState([]);
  const [works, setWorks] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [activeCodeTab, setActiveCodeTab] = useState("html");
  const [copySuccess, setCopySuccess] = useState("");
  const [worksOffset, setWorksOffset] = useState(0);
  const [projectsOffset, setProjectsOffset] = useState(0);
  const [loadingWorks, setLoadingWorks] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [hasMoreWorks, setHasMoreWorks] = useState(true);
  const [hasMoreProjects, setHasMoreProjects] = useState(true);
  const ITEMS_PER_PAGE = 4;

  useEffect(() => {
    setLoadingWorks(true);
    const fetchWorks = async () => {
      const worksQuery = `*[_type == \"works\"][${worksOffset}...${
        worksOffset + ITEMS_PER_PAGE
      }]`;
      const data = await client.fetch(worksQuery);
      if (data.length < ITEMS_PER_PAGE) setHasMoreWorks(false);
      else setHasMoreWorks(true);
      if (worksOffset === 0) setWorks(data);
      else setWorks((prev) => [...prev, ...data]);
      setLoadingWorks(false);
    };
    fetchWorks();
    // eslint-disable-next-line
  }, [worksOffset]);

  useEffect(() => {
    setLoadingProjects(true);
    const fetchProjects = async () => {
      const miniProjectsQuery = `*[_type == \"miniProject\"][${projectsOffset}...${
        projectsOffset + ITEMS_PER_PAGE
      }]`;
      const data = await client.fetch(miniProjectsQuery);
      if (data.length < ITEMS_PER_PAGE) setHasMoreProjects(false);
      else setHasMoreProjects(true);
      if (projectsOffset === 0) setProjects(data);
      else setProjects((prev) => [...prev, ...data]);
      setLoadingProjects(false);
    };
    fetchProjects();
    // eslint-disable-next-line
  }, [projectsOffset]);

  const handleProjectClick = (project) => {
    setSelectedProject(project);
    setShowCode(false);
    setIsFullscreen(false);
  };

  const handleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleCodeToggle = () => {
    setShowCode(!showCode);
  };

  const handleCopyCode = (code, type) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopySuccess(`${type.toUpperCase()} code copied!`);
      setTimeout(() => setCopySuccess(""), 2000);
    });
  };

  const closeModal = () => {
    setSelectedProject(null);
    setShowCode(false);
    setIsFullscreen(false);
  };

  const renderProjectPreview = () => {
    if (!selectedProject) return null;
    return <ProjectPreview selectedProject={selectedProject} />;
  };

  const renderCodeViewer = () => {
    if (!selectedProject || !showCode) return null;

    const codeTypes = [
      { key: "html", label: "HTML", code: selectedProject.html },
      { key: "css", label: "CSS", code: selectedProject.css },
      { key: "js", label: "JavaScript", code: selectedProject.js },
    ];

    return (
      <div className="code-viewer">
        <div className="code-tabs">
          {codeTypes.map(({ key, label }) => (
            <button
              key={key}
              className={`code-tab ${activeCodeTab === key ? "active" : ""}`}
              onClick={() => setActiveCodeTab(key)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="code-content">
          {codeTypes.map(({ key, code }) => (
            <div
              key={key}
              className={`code-block ${activeCodeTab === key ? "active" : ""}`}
            >
              <div className="code-header">
                <span className="code-language">{key.toUpperCase()}</span>
                <button
                  className="copy-button"
                  onClick={() => handleCopyCode(code, key)}
                >
                  <AiOutlineCopy />
                  Copy
                </button>
              </div>
              <pre className="code-pre">
                <code>{code}</code>
              </pre>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Works List */}
      <h2 className="head-text">
        Works <span>Projects</span> Gallery
      </h2>
      <p className="p-text projects-description">
        Explore my collection of works and mini projects showcasing various web
        development skills.
        <br />
        Click on any project to view details and code.
      </p>
      <div className="app__works-container">
        {loadingWorks && works.length === 0 ? (
          <div className="loader-center">
            <div className="loading-spinner"></div>
          </div>
        ) : (
          <div className="app__projects-grid">
            {works.map((work, index) => (
              <motion.div
                key={index}
                className="app__project-item"
                whileHover={{ y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <div className="app__project-img">
                  <LazyImage
                    src={urlFor(work.imgUrl)}
                    alt={work.title}
                    className="project-thumbnail"
                  />
                  <div className="app__project-overlay">
                    {work.projectLink && (
                      <a
                        href={work.projectLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <AiFillEye />
                        <span>View Work</span>
                      </a>
                    )}
                  </div>
                </div>
                <div className="app__project-content">
                  <h4 className="bold-text">{work.title}</h4>
                  <p className="p-text">{work.description}</p>
                  {work.codeLink && (
                    <a
                      href={work.codeLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="code-link"
                    >
                      <FaCode /> Code
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
        {hasMoreWorks && !loadingWorks && (
          <div className="load-more-center">
            <button
              className="load-more-btn"
              onClick={() => setWorksOffset(worksOffset + ITEMS_PER_PAGE)}
            >
              Load More
            </button>
          </div>
        )}
        {loadingWorks && works.length > 0 && (
          <div className="loader-center">
            <div className="loading-spinner"></div>
          </div>
        )}
      </div>

      <h2 className="head-text">
        Mini <span>Projects</span> Gallery
      </h2>
      <div className="app__projects-container">
        {loadingProjects && projects.length === 0 ? (
          <div className="loader-center">
            <div className="loading-spinner"></div>
          </div>
        ) : (
          <div className="app__projects-grid">
            {projects.map((project, index) => (
              <motion.div
                key={index}
                className="app__project-item"
                whileHover={{ y: -5 }}
                transition={{ duration: 0.3 }}
                onClick={() => handleProjectClick(project)}
              >
                <div className="app__project-img">
                  <LazyImage
                    src={urlFor(project.thumbnail)}
                    alt={project.title}
                    className="project-thumbnail"
                  />
                  <div className="app__project-overlay">
                    <AiFillEye />
                    <span>View Project</span>
                  </div>
                </div>
                <div className="app__project-content">
                  <h4 className="bold-text">{project.title}</h4>
                  <p className="p-text">{project.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        )}
        {hasMoreProjects && !loadingProjects && (
          <div className="load-more-center">
            <button
              className="load-more-btn"
              onClick={() => setProjectsOffset(projectsOffset + ITEMS_PER_PAGE)}
            >
              Load More
            </button>
          </div>
        )}
        {loadingProjects && projects.length > 0 && (
          <div className="loader-center">
            <div className="loading-spinner"></div>
          </div>
        )}
      </div>

      {/* Project Modal */}
      {selectedProject && (
        <ProjectModal
          selectedProject={selectedProject}
          showCode={showCode}
          renderCodeViewer={renderCodeViewer}
          handleCodeToggle={handleCodeToggle}
          handleFullscreen={handleFullscreen}
          isFullscreen={isFullscreen}
          closeModal={closeModal}
        />
      )}
      <QuickLinks />
      {/* Copy Success Message */}
      {copySuccess && <div className="copy-success-message">{copySuccess}</div>}
    </>
  );
};

export default AppWrap(
  MotionWrap(Project, "app__projects"),
  "projects",
  "app__whitebg"
);
